<template>
  <div>
    <v-card class="mx-2 px-2">
      <v-toolbar dark color="primary">
        <v-toolbar-title>Tiempos de Atención</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-alert color="blue-grey" dark outlined dense>
          Muestra el tiempo de atención de cada profesional por día dentro de
          la institución, calculado a partir de los turnos atendidos.
          Seleccione un rango de fechas y, si lo desea, un profesional para
          filtrar los resultados.
        </v-alert>

        <v-row>
          <v-col cols="12" md="4" sm="6">
            <v-menu
              v-model="menuInicio"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="fechaInicioFormatted"
                  label="Fecha inicio"
                  readonly
                  v-on="on"
                  prepend-icon="mdi-calendar"
                  clearable
                  @click:clear="fechaInicio = ''"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fechaInicio"
                locale="es-ar"
                @input="menuInicio = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4" sm="6">
            <v-menu
              v-model="menuFin"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="fechaFinFormatted"
                  label="Fecha fin"
                  readonly
                  v-on="on"
                  prepend-icon="mdi-calendar"
                  clearable
                  @click:clear="fechaFin = ''"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fechaFin"
                locale="es-ar"
                @input="menuFin = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4" sm="6">
            <v-select
              :items="profsBuscar"
              item-text="nombreProfesional"
              item-value="idProfesional"
              menu-props="auto"
              label="Profesional"
              hide-details
              prepend-icon="mdi-account"
              v-model="prof"
              dense
              no-data-text="No hay Profesionales configurados"
              class="mt-5"
            ></v-select>
          </v-col>
        </v-row>

        <v-alert
          v-if="errorFechas"
          type="error"
          dense
          outlined
          class="mt-2"
        >
          {{ errorFechas }}
        </v-alert>

        <v-row class="mt-2" v-if="consultado && !resultados.length">
          <v-col cols="12">
            <v-alert type="info" outlined dense>
              No se encontraron registros para los filtros seleccionados.
            </v-alert>
          </v-col>
        </v-row>

        <v-row v-if="resultados.length" class="mt-2">
          <v-col cols="12">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Profesional</th>
                    <th class="text-left">Fecha</th>
                    <th class="text-left">Cantidad de turnos</th>
                    <th class="text-left">Tiempo total</th>
                    <th class="text-left">Tiempo promedio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in resultadosOrdenados" :key="i">
                    <td>{{ item.nombreProfesional || "Sin profesional" }}</td>
                    <td>{{ formatFecha(item.fecha) }}</td>
                    <td>{{ item.cantidadTurnos }}</td>
                    <td>{{ formatDuracion(item.minutosTotales) }}</td>
                    <td>{{ formatDuracion(item.minutosPromedio) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn
          color="primary"
          @click.stop="consultar"
          :loading="loading"
          :disabled="loading"
        >
          <v-icon left class="mx-1">mdi-magnify</v-icon>Consultar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="error" @click.stop="salir">Salir</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import axios from "../../axios-auth.js";
import router from "../../router/index.js";
import moment from "moment";

export default {
  data() {
    return {
      profsBuscar: [],
      prof: 0,
      fechaInicio: "",
      fechaFin: "",
      menuInicio: false,
      menuFin: false,
      errorFechas: "",
      loading: false,
      consultado: false,
      resultados: [],
    };
  },
  computed: {
    fechaInicioFormatted() {
      return this.fechaInicio ? moment(this.fechaInicio).format("DD/MM/YYYY") : "";
    },
    fechaFinFormatted() {
      return this.fechaFin ? moment(this.fechaFin).format("DD/MM/YYYY") : "";
    },
    resultadosOrdenados() {
      return [...this.resultados].sort((a, b) => {
        const nombreA = (a.nombreProfesional || "").toLowerCase();
        const nombreB = (b.nombreProfesional || "").toLowerCase();
        if (nombreA !== nombreB) return nombreA < nombreB ? -1 : 1;
        return new Date(a.fecha) - new Date(b.fecha);
      });
    },
  },
  methods: {
    formatFecha(fecha) {
      return fecha ? moment(fecha).format("DD/MM/YYYY") : "";
    },
    formatDuracion(minutosTotales) {
      // Trabajamos en segundos para no perder precisión en el promedio
      // (p. ej. 10 min / 3 turnos = 3.33 min => "3 min 20 s").
      const totalSeg = Math.max(0, Math.round((minutosTotales || 0) * 60));
      const horas = Math.floor(totalSeg / 3600);
      const minutos = Math.floor((totalSeg % 3600) / 60);
      const segundos = totalSeg % 60;

      const partes = [];
      if (horas > 0) partes.push(`${horas} h`);
      if (minutos > 0) partes.push(`${minutos} min`);
      if (segundos > 0) partes.push(`${segundos} s`);
      // Si todo es 0 (o redondeó a 0), mostramos "0 min" en vez de vacío.
      return partes.length ? partes.join(" ") : "0 min";
    },
    validarFechas() {
      this.errorFechas = "";
      if (!this.fechaInicio || !this.fechaFin) {
        this.errorFechas = "Debe indicar la fecha de inicio y la fecha de fin.";
        return false;
      }
      if (moment(this.fechaInicio).isAfter(moment(this.fechaFin))) {
        this.errorFechas =
          "La fecha de inicio no puede ser posterior a la fecha de fin.";
        return false;
      }
      return true;
    },
    consultar() {
      if (!this.validarFechas()) {
        return;
      }

      this.loading = true;
      this.consultado = false;
      const idInstitucion =
        this.$store.state.usuario.institucion.idInstitucion;
      const params = {
        idInstitucion: idInstitucion,
        fechaInicio: this.fechaInicio,
        fechaFin: this.fechaFin,
      };
      if (this.prof) {
        params.idProfesional = this.prof;
      }

      let self = this;
      axios
        .get("/Estadisticas/TiemposAtencion?", { params })
        .then((response) => {
          self.resultados = response.data || [];
          self.consultado = true;
        })
        .catch(() => {
          self.resultados = [];
          self.consultado = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    cargarProfesionales() {
      const profData = {
        usuario: this.$store.state.usuario.usuario,
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
      };
      let self = this;
      axios
        .get("/Turnos/BuscarProfesionalesAsociados?", {
          params: {
            usuario: profData.usuario,
            idInstitucion: profData.idInstitucion,
          },
        })
        .then((response) => {
          self.profsBuscar = response.data.profesionales || [];
          self.profsBuscar.unshift({
            idProfesional: 0,
            nombreProfesional: "TODOS",
          });
          self.prof = 0;
        });
    },
    salir() {
      router.push("/Home");
    },
  },
  created() {
    this.cargarProfesionales();
    const hoy = moment().format("YYYY-MM-DD");
    this.fechaInicio = hoy;
    this.fechaFin = hoy;
  },
};
</script>
